import { useEffect, useMemo, useState } from "react";
import { API } from "../../../cache";
import "../../../MasterViewV2.css";
import "../styles/UtilizationTab.css";

function fmtH(v) {
  const n = Number(v) || 0;
  return Number.isInteger(n) ? `${n}h` : `${n.toFixed(1)}h`;
}

// Same rounding as fmtH but no unit suffix — used for the on-bar value
// labels, which are already grouped under an "hours" axis/legend, so
// repeating "h" on every single bar is redundant.
function fmtNum(v) {
  const n = Number(v) || 0;
  return Number.isInteger(n) ? `${n}` : n.toFixed(1);
}

const COLOR_ESTIMATED     = "#C9BFA0"; // muted tan — reference ceiling, not a status/identity color
const COLOR_EXPECTED      = "#9CA3AF"; // grey, as requested — the pace target
const COLOR_ACTUAL_OK     = "#4a7c5c"; // same green already used for "Closed" in the Inflow/Outflow chart
const COLOR_ACTUAL_OVER   = "#B03A2E"; // established --mv2-danger red
const COLOR_ACTUAL_BEHIND = "#C8842E"; // established accent orange — under-pace warning, distinct from red/green

const PACE_STATUS_LABEL = {
  over:    "Over budget / over pace",
  behind:  "May be behind schedule",
  on_pace: "On pace",
};

// Greedy word-wrap into at most `maxLines` lines, each capped at
// `maxCharsPerLine` — overflow beyond maxLines is merged into the last line
// (truncated with an ellipsis if it's still too long). Used to fit long
// epic names into fully-vertical, stacked, multi-line axis labels.
function wrapLines(text, maxLines = 3, maxCharsPerLine = 13) {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";
  for (const w of words) {
    const candidate = current ? `${current} ${w}` : w;
    if (candidate.length > maxCharsPerLine && current) {
      lines.push(current);
      current = w;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);

  if (lines.length > maxLines) {
    const head = lines.slice(0, maxLines - 1);
    const tail = lines.slice(maxLines - 1).join(" ");
    const truncatedTail = tail.length > maxCharsPerLine ? `${tail.slice(0, maxCharsPerLine - 1)}…` : tail;
    return [...head, truncatedTail];
  }
  return lines;
}

// One continuous SVG bar chart — same structural pattern as the Defects
// Dashboard's "Inflow vs outflow" ComboChart (DefectsViewV2Page.jsx): a
// shared Y-axis with gridlines, one X slot per category, bars rising from
// a common baseline, category labels along the bottom. Adapted here for
// categorical (epic) data instead of a continuous date axis: epics keep a
// FIXED per-slot width (not squeezed to fit like the date chart, whose
// bars can get thinner as the range grows) and the container scrolls
// horizontally instead — epic names are long, unlike short date labels, so
// every one stays legible (rotated, not skipped) rather than thinning out.
// One shared Y-scale across all epics (matching the reference chart's own
// single scale for its whole date range), not a per-epic scale — expected
// since real epic sizes vary far more than day-to-day defect counts, and
// this is the same tradeoff the reference chart already makes.
function UtilizationChart({ rows }) {
  const [infoPopup, setInfoPopup] = useState(null); // { row, x, y }
  const closeInfoPopup = () => setInfoPopup(null);

  const groupW = 78;     // px per epic slot
  const barW = 16;       // px per individual bar
  const barGap = 3;
  const pad = { top: 46, right: 20, bottom: 130, left: 52 };
  const H = 420;
  const cH = H - pad.top - pad.bottom;
  const W = pad.left + pad.right + rows.length * groupW;

  const overallMax = Math.max(
    ...rows.map((r) => Math.max(r.estimated_hours, r.actual_hours)),
    1
  );
  // "Nice" rounded scale ceiling + 4 evenly spaced ticks, same idea as the
  // reference chart's yTicks loop.
  const magnitude = Math.pow(10, Math.floor(Math.log10(overallMax || 1)));
  const niceMax = Math.ceil(overallMax / (magnitude / 2)) * (magnitude / 2);
  const yTicks = 4;

  const xOf = (i) => pad.left + i * groupW + groupW / 2;
  const yOf = (v) => pad.top + cH - (v / niceMax) * cH;
  const barH = (v) => Math.max(0, cH - (yOf(v) - pad.top));

  return (
    <div className="ut-chart-scroll-x" onClick={closeInfoPopup}>
      <svg
        className="ut-svg"
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        preserveAspectRatio="xMinYMin meet"
      >
        {/* Grid lines + Y axis */}
        {Array.from({ length: yTicks + 1 }, (_, j) => {
          const y = pad.top + (cH / yTicks) * j;
          const val = Math.round(niceMax * (1 - j / yTicks));
          return (
            <g key={j}>
              <line x1={pad.left} x2={W - pad.right} y1={y} y2={y} stroke="#e3e6ea" strokeWidth={1} />
              <text x={pad.left - 8} y={y + 4} textAnchor="end" fill="#999" fontFamily="JetBrains Mono, monospace" fontSize={10}>
                {val}
              </text>
            </g>
          );
        })}

        {/* Bars + bottom labels */}
        {rows.map((r, i) => {
          const cx = xOf(i);
          const x0 = cx - (barW * 3 + barGap * 2) / 2;
          const actualColor =
            r.pace_status === "over" ? COLOR_ACTUAL_OVER :
            r.pace_status === "behind" ? COLOR_ACTUAL_BEHIND :
            COLOR_ACTUAL_OK;
          const bars = [
            { v: r.estimated_hours, color: COLOR_ESTIMATED },
            { v: r.expected_hours,  color: COLOR_EXPECTED },
            { v: r.actual_hours,    color: actualColor },
          ];
          // Fully vertical label, wrapped into up to 3 lines — each line is
          // its own text element, anchored at a FIXED point just below the
          // baseline (top-aligned: every epic's label starts flush next to
          // its own bars, then hangs down by however much its own text
          // needs — not anchored at a shared bottom point with a variable
          // gap above short labels, which is what "bottom aligned" would
          // give instead). textAnchor="end" + rotate(-90) puts the LAST
          // character of the line at that fixed anchor (near the baseline)
          // and grows earlier characters downward/away — so the line reads
          // bottom-to-top, the standard "tilt your head left to read"
          // vertical-label convention (not "tilt right", which is what
          // textAnchor="start" + rotate(90) gives instead). The 3 lines sit
          // side by side as parallel vertical columns, centered under this
          // epic's bars.
          const lines = wrapLines(r.epic_name);
          const colSpacing = 11;
          const anchorY = pad.top + cH + 10;
          const labelColor =
            r.pace_status === "over" ? COLOR_ACTUAL_OVER :
            r.pace_status === "behind" ? COLOR_ACTUAL_BEHIND :
            "#666";
          const infoX = cx;
          const infoY = 12;
          return (
            <g key={r.epic_id}>
              <g
                className="ut-info-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setInfoPopup({ row: r, x: e.clientX, y: e.clientY });
                }}
              >
                <circle cx={infoX} cy={infoY} r={7} fill="#EDE8DC" stroke="#8B8F99" strokeWidth={1} />
                <text
                  x={infoX}
                  y={infoY + 3}
                  textAnchor="middle"
                  fontFamily="Georgia, serif"
                  fontStyle="italic"
                  fontWeight={700}
                  fontSize={9.5}
                  fill="#5C6069"
                >
                  i
                </text>
              </g>
              {bars.map((b, bi) => {
                const x = x0 + bi * (barW + barGap);
                const h = barH(b.v);
                const barTop = yOf(b.v);
                const valueAnchorX = x + barW / 2;
                const valueAnchorY = Math.max(barTop - 4, pad.top - 6);
                // The value label always renders (shows "0" for a zero
                // value instead of leaving a blank gap with no bar and no
                // indication of why) — only the rect itself is skipped when
                // there's no height to draw.
                return (
                  <g key={bi}>
                    {b.v > 0 && (
                      <rect x={x} y={barTop} width={barW} height={h} fill={b.color}>
                        <title>{r.epic_name} — {fmtH(b.v)}</title>
                      </rect>
                    )}
                    <text
                      x={valueAnchorX}
                      y={valueAnchorY}
                      textAnchor="start"
                      fill="#555"
                      fontFamily="JetBrains Mono, monospace"
                      fontSize={8.5}
                      transform={`rotate(-90 ${valueAnchorX} ${valueAnchorY})`}
                    >
                      {fmtNum(b.v)}
                    </text>
                  </g>
                );
              })}
              {lines.map((line, li) => {
                const lx = cx - ((lines.length - 1) * colSpacing) / 2 + li * colSpacing;
                return (
                  <text
                    key={li}
                    x={lx}
                    y={anchorY}
                    textAnchor="end"
                    fill={labelColor}
                    fontFamily="JetBrains Mono, monospace"
                    fontSize={10}
                    fontWeight={r.pace_status !== "on_pace" ? 700 : 400}
                    transform={`rotate(-90 ${lx} ${anchorY})`}
                  >
                    {line}
                  </text>
                );
              })}
            </g>
          );
        })}

        {/* Baseline */}
        <line x1={pad.left} x2={W - pad.right} y1={pad.top + cH} y2={pad.top + cH} stroke="#333" strokeWidth={1.5} />
      </svg>

      {infoPopup && (
        <div
          className="ut-info-popup"
          style={{ left: infoPopup.x, top: infoPopup.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="ut-info-popup__head">
            <span className="ut-info-popup__title">{infoPopup.row.epic_name}</span>
            <button type="button" className="ut-info-popup__close" onClick={closeInfoPopup}>✕</button>
          </div>
          <div className="ut-info-popup__row"><span>Customer</span><span>{infoPopup.row.customer_name || "—"}</span></div>
          <div className="ut-info-popup__row"><span>Project</span><span>{infoPopup.row.project_name || "—"}</span></div>
          <div className="ut-info-popup__row"><span>Start date</span><span>{infoPopup.row.start_date || "—"}</span></div>
          <div className="ut-info-popup__row"><span>End date</span><span>{infoPopup.row.end_date || "—"}</span></div>
        </div>
      )}
    </div>
  );
}

const SORT_COLUMNS = [
  { key: "epic_name",       label: "Epic" },
  { key: "customer_name",   label: "Customer" },
  { key: "estimated_hours", label: "Estimated" },
  { key: "actual_hours",    label: "Actual" },
  { key: "expected_hours",  label: "Expected" },
];

export default function UtilizationTab({ showToast, search = "" }) {
  const [loading, setLoading] = useState(true);
  const [asOf, setAsOf] = useState("");
  const [rows, setRows] = useState([]);
  // True for every non-admin caller — the API scopes epics down to the
  // projects the caller is allocated to (project_user_mapping/
  // customer_user_mapping), so an empty result here means "no projects
  // allocated to you yet," not "nothing is happening org-wide." Admins get
  // the unscoped org-wide view and this is false for them.
  const [scoped, setScoped] = useState(false);
  const [statusFilter, setStatusFilter] = useState("over"); // "all" | "over" | "ok"
  // "epic_range" (default) sums actual_hours only within this epic's own
  // start/end dates — "all_time" has no date filter at all, matching
  // evaluate_epic_budget()'s existing convention. These can legitimately
  // differ a lot for an epic whose linked tasks/defects carry effort logged
  // in prior periods (e.g. a recurring monthly support epic) — this toggle
  // exists specifically so that's a deliberate choice, not a hidden default.
  const [actualScope, setActualScope] = useState("epic_range"); // "epic_range" | "all_time"
  const [viewMode, setViewMode] = useState("chart"); // "chart" | "table"
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/dashboard/utilization?scope=${actualScope}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        setAsOf(data?.as_of || "");
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setScoped(!!data?.scoped);
      })
      .catch(() => showToast?.("Failed to load utilization data", "warn"))
      .finally(() => setLoading(false));
  }, [showToast, actualScope]);

  const filteredRows = useMemo(() => {
    let out = rows;
    if (statusFilter === "over") out = out.filter((r) => r.is_over);
    else if (statusFilter === "ok") out = out.filter((r) => !r.is_over);
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((r) =>
        (r.epic_name || "").toLowerCase().includes(q) ||
        (r.customer_name || "").toLowerCase().includes(q) ||
        (r.epic_code || "").toLowerCase().includes(q)
      );
    }
    return out;
  }, [rows, search, statusFilter]);

  // Table view is independently sortable; chart view always keeps the API's
  // own is_over-first order (re-sorting the chart would defeat the point of
  // "over-pace epics shown first").
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  };
  const sortedTableRows = useMemo(() => {
    if (!sortKey) return filteredRows;
    const rowsCopy = [...filteredRows];
    rowsCopy.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return rowsCopy;
  }, [filteredRows, sortKey, sortDir]);

  const overCount = rows.filter((r) => r.is_over).length;

  return (
    <div className="ut-root">
      <div className="ut-toolbar">
        <div className="ut-toolbar-right">
          {asOf && <span className="ut-as-of">As of {asOf}</span>}
          {scoped && <span className="ut-as-of" title="Showing only epics under projects you're allocated to">Your allocated projects</span>}
          {overCount > 0 && <span className="ut-over-badge">{overCount} over budget / over pace</span>}
          <div className="mv2-view-toggle" title="Date window for the Actual hours calculation">
            <button
              className={`mv2-view-btn ut-status-btn${actualScope === "epic_range" ? " mv2-view-btn--active" : ""}`}
              onClick={() => setActualScope("epic_range")}
              title="Only effort logged within this epic's own start/end dates"
            >
              Epic range
            </button>
            <button
              className={`mv2-view-btn ut-status-btn${actualScope === "all_time" ? " mv2-view-btn--active" : ""}`}
              onClick={() => setActualScope("all_time")}
              title="All effort ever logged against this epic, no date filter"
            >
              All time
            </button>
          </div>
          <div className="mv2-view-toggle">
            <button
              className={`mv2-view-btn ut-status-btn${statusFilter === "all" ? " mv2-view-btn--active" : ""}`}
              onClick={() => setStatusFilter("all")}
            >
              All
            </button>
            <button
              className={`mv2-view-btn ut-status-btn${statusFilter === "over" ? " mv2-view-btn--active" : ""}`}
              onClick={() => setStatusFilter("over")}
            >
              Over budget
            </button>
            <button
              className={`mv2-view-btn ut-status-btn${statusFilter === "ok" ? " mv2-view-btn--active" : ""}`}
              onClick={() => setStatusFilter("ok")}
            >
              Within limits
            </button>
          </div>
          <div className="mv2-view-toggle">
            <button
              className={`mv2-view-btn ut-mode-btn${viewMode === "chart" ? " mv2-view-btn--active" : ""}`}
              onClick={() => setViewMode("chart")}
            >
              Chart
            </button>
            <button
              className={`mv2-view-btn ut-mode-btn${viewMode === "table" ? " mv2-view-btn--active" : ""}`}
              onClick={() => setViewMode("table")}
            >
              Table
            </button>
          </div>
        </div>
      </div>

      <div className="ut-legend">
        <span className="ut-legend-item"><span className="ut-swatch" style={{ background: COLOR_ESTIMATED }} />Estimated</span>
        <span className="ut-legend-item"><span className="ut-swatch" style={{ background: COLOR_EXPECTED }} />Expected (to date)</span>
        <span className="ut-legend-item"><span className="ut-swatch" style={{ background: COLOR_ACTUAL_OK }} />Actual (on pace)</span>
        <span className="ut-legend-item"><span className="ut-swatch" style={{ background: COLOR_ACTUAL_OVER }} />Actual — over budget or over pace</span>
        <span className="ut-legend-item"><span className="ut-swatch" style={{ background: COLOR_ACTUAL_BEHIND }} />Actual — may be behind schedule</span>
      </div>

      {loading ? (
        <div className="ut-state">Loading…</div>
      ) : filteredRows.length === 0 ? (
        <div className="ut-state">
          {rows.length === 0 && scoped
            ? "No projects are allocated to you yet — contact your admin to get project access."
            : "No epics found."}
        </div>
      ) : viewMode === "chart" ? (
        <UtilizationChart rows={filteredRows} />
      ) : (
        <div className="ut-table-wrap">
          <table className="ut-table">
            <thead>
              <tr>
                <th className="ut-th ut-th--num">#</th>
                {SORT_COLUMNS.map((c) => (
                  <th key={c.key} className="ut-th" onClick={() => toggleSort(c.key)}>
                    {c.label} {sortKey === c.key ? (sortDir === "asc" ? "↑" : "↓") : ""}
                  </th>
                ))}
                <th className="ut-th">Status</th>
              </tr>
            </thead>
            <tbody>
              {sortedTableRows.map((r, i) => (
                <tr
                  key={r.epic_id}
                  className={
                    r.pace_status === "over" ? "ut-row--over" :
                    r.pace_status === "behind" ? "ut-row--behind" : ""
                  }
                >
                  <td className="ut-td ut-td--num">{i + 1}</td>
                  <td className="ut-td">{r.epic_name}</td>
                  <td className="ut-td">{r.customer_name}</td>
                  <td className="ut-td ut-td--num">{fmtH(r.estimated_hours)}</td>
                  <td className="ut-td ut-td--num">{fmtH(r.actual_hours)}</td>
                  <td className="ut-td ut-td--num">{fmtH(r.expected_hours)}</td>
                  <td className="ut-td">{PACE_STATUS_LABEL[r.pace_status] || "On pace"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
